/**
 * @fileoverview Settings › Preferences › Notifications: the emails the
 * signed-in administrator receives, each a switch that saves as it flips.
 *
 * Optimistic: the switch moves at once and moves back, with a toast, if the
 * save fails. Until the preferences have been read the switches are disabled,
 * so nobody flips one against a state that is not theirs.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { ORG_EMAIL_PREFERENCES } from "@/lib/orgEmailPreferences";
import { SettingsRow, SettingsSection, Toggle } from "@/components/settings/SettingsSection";

export default function OrgEmailPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let on = true;
    orgApi
      .get("/org/auth/email-preferences")
      .then((resp) => {
        if (on) setPrefs(resp.data?.preferences || null);
      })
      .catch(() => {
        if (on) setFailed(true);
      });
    return () => {
      on = false;
    };
  }, []);

  const flip = async (key, next) => {
    const before = prefs;
    setPrefs((p) => ({ ...p, [key]: next }));
    try {
      const resp = await orgApi.put("/org/auth/email-preferences", { preferences: { [key]: next } });
      setPrefs(resp.data?.preferences || { ...before, [key]: next });
    } catch (err) {
      setPrefs(before);
      toast.error(getOrgErrorMessage(err, "Couldn't change that email"));
    }
  };

  return (
    <SettingsSection title="Notifications" testId="org-email-preferences" className="mb-8">
      {failed && (
        <div className="border-b border-[var(--studio-divider)] px-5 py-4 font-sans text-[13px] text-[var(--state-danger)] md:px-6">
          Your email settings couldn&rsquo;t be loaded. Reload the page to try again.
        </div>
      )}
      {ORG_EMAIL_PREFERENCES.map((p, i) => (
        <SettingsRow
          key={p.key}
          title={p.label}
          desc={p.desc}
          last={i === ORG_EMAIL_PREFERENCES.length - 1}
          testId={`email-pref-${p.key}`}
          trailing={
            <Toggle
              on={prefs ? prefs[p.key] !== false : false}
              disabled={!prefs}
              onChange={(next) => flip(p.key, next)}
              label={p.label}
              testId={`email-pref-toggle-${p.key}`}
            />
          }
        />
      ))}
    </SettingsSection>
  );
}
